import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { Badge } from '@epoch-ui/components/Badge';
import { Checkbox } from '@epoch-ui/components/Checkbox/checkbox';
import { Label } from '@epoch-ui/components/Form';
import { SearchInput } from '@epoch-ui/components/SearchInput/SearchInput';
import { inviteGuestsApi } from '@event-editor/api/invite-guests';
import { formatDistrolistMember } from 'utils/format';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

import { useInviteGuestsModalContext } from '../../hooks/useInviteGuestsModalContext';

const SEARCH_DEBOUNCE_MS = 500;

export const InviteCommunitySelector: React.FC = () => {
  const event = useEditableEvent();
  const { selectedCommunities, toggleCommunity } = useInviteGuestsModalContext();
  const [searchText, setSearchText] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: eventEditorQueryKeys.eventInvitableCommunities(event.id!, searchText),
    queryFn: () =>
      inviteGuestsApi.getInvitableCommunities({
        eventId: event.id!,
        searchText
      }),
    enabled: !!event.id
  });

  const communities = data?.list ?? [];

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-mt-4">
        <SearchInput
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          debounce={SEARCH_DEBOUNCE_MS}
          className="tw-w-full"
        />
      </div>

      {isLoading ? (
        <div className="tw-flex tw-h-32 tw-items-center tw-justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="tw-relative tw-mt-2 tw-h-full tw-overflow-y-scroll tw-pr-1">
          <ul className="tw-mb-0 tw-max-h-[408px] tw-min-h-16 tw-list-none tw-p-0">
            {communities.map((community) => {
              const selected = !!selectedCommunities.find((c) => c.id === community.id);
              const isCommunityInvited = !!event.communityInviteeIds?.includes(
                Number(community.id)
              );
              const checkboxId = `community-${community.id}`;

              return (
                <li className="tw-mx-0 tw-my-4 first:tw-mt-2" key={community.id}>
                  <div className="tw-flex tw-cursor-pointer tw-flex-row tw-items-center tw-justify-center">
                    <Checkbox
                      id={checkboxId}
                      checked={selected}
                      disabled={isCommunityInvited}
                      onCheckedChange={() => toggleCommunity(community)}
                    />

                    <Label
                      htmlFor={checkboxId}
                      className={`tw-ml-3 tw-mr-1 tw-flex-1 ${
                        isCommunityInvited ? 'tw-opacity-40' : 'tw-opacity-100'
                      }`}
                    >
                      <p className="tw-mb-0 tw-text-sm tw-leading-5 tw-text-black">
                        {community.name}
                      </p>
                      <p className="tw-mb-0 tw-text-sm tw-leading-5 tw-text-primary-grey">
                        {formatDistrolistMember(community.memberCount || 0)}
                      </p>
                    </Label>

                    {isCommunityInvited && <Badge variant="secondary">Already in guest list</Badge>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
